/**
 * Shared utilities for {@link SportClubRow}, {@link SportClubCard} and {@link SportClubsDataTable}.
 */
import type { ListSportClubsParams, SportClub } from '@/types/sport-club.types';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import {
  DEFAULT_SPORT_CLUB_FILTERS,
  SPORT_CLUB_ACTION_IDS,
  SPORT_CLUB_FILTER_IDS,
  SPORT_CLUBS_MENU_ELEMENT_ID,
  getSportTypeConfig,
  type SportClubFilters,
  type SportClubTypeConfig,
} from './sport-club-configs';

/** Row / card display fields derived once from a sport club. */
interface DerivedSportClubDisplay {
  typeConfig: SportClubTypeConfig;
  TypeIcon: SportClubTypeConfig['icon'];
  websiteLabel: string | undefined;
}

/** Detail-panel extras on top of {@link DerivedSportClubDisplay}. */
interface DerivedSportClubDetailDisplay extends DerivedSportClubDisplay {
  cityLine: string | undefined;
  createdAtLabel: string | undefined;
  updatedAtLabel: string | undefined;
}

/** RBAC flags for sport-club list actions and filters. */
export function useSportClubPageActions() {
  const { elements } = useInterfaceElements(SPORT_CLUBS_MENU_ELEMENT_ID);

  const find = (elementId: string) => elements.some((el) => el.elementId === elementId);

  return {
    canCreateClub: find(SPORT_CLUB_ACTION_IDS.create),
    canViewClub: find(SPORT_CLUB_ACTION_IDS.view),
    canEditClub: find(SPORT_CLUB_ACTION_IDS.edit),
    canDeleteClub: find(SPORT_CLUB_ACTION_IDS.delete),
    canExportClubs: find(SPORT_CLUB_ACTION_IDS.exportExcel),
    canUseClubSearchFilter: find(SPORT_CLUB_FILTER_IDS.search),
    canUseClubCityFilter: find(SPORT_CLUB_FILTER_IDS.city),
    canUseClubSportTypeFilter: find(SPORT_CLUB_FILTER_IDS.sportType),
  };
}

/** React Query keys for sport-club list / detail. */
export const sportClubsKeys = {
  all: ['sport-clubs'] as const,
  list: (params?: ListSportClubsParams) => ['sport-clubs', 'list', params] as const,
  detail: (id: string) => ['sport-clubs', 'detail', id] as const,
};

/** French short date (`dd/mm/yyyy`). */
function formatSportClubDate(dateStr: string | null | undefined): string | undefined {
  if (!dateStr) return undefined;
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** City line from zip + city. */
function formatSportClubCityLine(
  zipCode: string | null | undefined,
  city: string | null | undefined,
): string | undefined {
  if (!city) return undefined;
  return [zipCode, city].filter(Boolean).join(' ');
}

/** Display URL without `http(s)://`. */
function stripSportClubWebsiteProtocol(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

/** Map UI filters to API query params (no pagination). */
export function toSportClubApiFilterParams(
  filters: SportClubFilters,
): Omit<ListSportClubsParams, 'page' | 'pageSize'> {
  return {
    search: filters.search,
    city: filters.city,
    sportType: filters.sportType,
  };
}

/** Map UI filters + pagination to list query params. */
export function toSportClubListParams(
  filters: SportClubFilters,
  currentPage: number,
  itemsPerPage: number,
): ListSportClubsParams {
  return {
    ...toSportClubApiFilterParams(filters),
    page: currentPage,
    pageSize: itemsPerPage,
  };
}

/** Whether filters differ from the table defaults. */
export function hasSportClubActiveFilters(
  filters: SportClubFilters,
  defaultFilters: SportClubFilters = DEFAULT_SPORT_CLUB_FILTERS,
): boolean {
  return JSON.stringify(filters) !== JSON.stringify(defaultFilters);
}

/** Block Excel export when the filtered list is empty. */
export function isSportClubEmptyForExport(isLoading: boolean, totalItems: number): boolean {
  return !isLoading && totalItems === 0;
}

/** Export button tooltip (empty-list vs default). */
export function getSportClubExportTooltip(isEmptyForExport: boolean, isExporting: boolean): string {
  if (isEmptyForExport && !isExporting) return 'Aucun club à exporter.';
  return 'Exporter en Excel';
}

/** Quoted club name for the delete modal. */
export function formatSportClubDeleteModalItemName(
  club: SportClub | null | undefined,
): string | undefined {
  return club ? `"${club.name}"` : undefined;
}

/** Expanded-row colspan matching the sport-clubs column count. */
export function getSportClubExpandedColSpan(): number {
  return 6;
}

/** Shared row / card display payload. */
export function deriveSportClubDisplay(club: SportClub): DerivedSportClubDisplay {
  const typeConfig = getSportTypeConfig(club.sportType);
  return {
    typeConfig,
    TypeIcon: typeConfig.icon,
    websiteLabel: club.website ? stripSportClubWebsiteProtocol(club.website) : undefined,
  };
}

/** Detail-panel display payload (includes city line + timestamps). */
export function deriveSportClubDetailDisplay(club: SportClub): DerivedSportClubDetailDisplay {
  return {
    ...deriveSportClubDisplay(club),
    cityLine: formatSportClubCityLine(club.zipCode, club.city),
    createdAtLabel: formatSportClubDate(club.createdAt),
    updatedAtLabel: formatSportClubDate(club.updatedAt),
  };
}
